import get from 'lodash/get';
import { useCallback, useEffect, useMemo } from 'react';

import { useDrawingShapeContext } from '../../drawingShapeContext';

import { useCornerRadii } from '~/api/easy-estimator-api/corner-radii-api/hooks/useCornerRadii';
import { useSelectedCornerRadius } from '~/api/easy-estimator-api/corner-radii-api/hooks/useSelectedCornerRadius';
import { useDiagonalCorners } from '~/api/easy-estimator-api/diagonal-corners-api/useDiagonalCorners';
import { CANVAS_OBJECT_TYPE_PREFIX, SELECTION_CIRCLES } from '~/core/constants';
import { drawingTool } from '~/core/engine/DrawingTool';
import { CircleAction, DiagonalCornerOptions, StepName } from '~/core/enums';
import { getCornerType } from '~/core/helpers';
import { DrawingShape } from '~/core/models';
import { useAppSelector } from '~/redux/hooks';
import { selectSelectedCustomSide } from '~/redux/slices/customSide';
import { selectSelectedDiagonalCorner } from '~/redux/slices/diagonalCorner';
import {
  selectFabricCanvas,
  selectSelectionCircles,
} from '~/redux/slices/fabric';
import { selectCurrentStep } from '~/redux/slices/stepper';

type SelectionCircleStep =
  | StepName.designCornerRadius
  | StepName.designDiagonalCorner
  | StepName.designCustomSide;

export const useCanvasSelectionCircles = () => {
  const canvas = useAppSelector(selectFabricCanvas);
  const currentStep = useAppSelector(selectCurrentStep);
  const selectionCircles = useAppSelector(selectSelectionCircles);
  const selectedDiagonalCorner = useAppSelector(selectSelectedDiagonalCorner);
  const selectedCustomSide = useAppSelector(selectSelectedCustomSide);
  const { defaultValue: defaultCornerRadii } = useCornerRadii();
  const { defaultValue: defaultDiagonalCorner } = useDiagonalCorners();

  const { selectedValue: selectedCornerRadius } = useSelectedCornerRadius();

  const {
    getters: { drawingShapes },
  } = useDrawingShapeContext();

  const selectionCircleRenderByStep: Record<
    SelectionCircleStep,
    ((drawingShape: DrawingShape) => void) | undefined
  > = useMemo(
    () => ({
      [StepName.designCornerRadius]: selectedCornerRadius
        ? (drawingShape) =>
            drawingTool.addCirclesToOutlinePointsOfShape(
              canvas,
              drawingShape,
              selectedCornerRadius,
              DiagonalCornerOptions.OUTSIDE_CORNER,
              {
                removalCorner:
                  selectionCircles.action === CircleAction.REMOVE
                    ? defaultCornerRadii
                    : undefined,
              }
            )
        : undefined,
      [StepName.designDiagonalCorner]: selectedDiagonalCorner
        ? (drawingShape) =>
            drawingTool.addCirclesToOutlinePointsOfShape(
              canvas,
              drawingShape,
              selectedDiagonalCorner,
              selectionCircles.action === CircleAction.REMOVE
                ? DiagonalCornerOptions.REMOVE_DIAGONAL_CORNER
                : getCornerType(selectedDiagonalCorner),
              {
                removalCorner:
                  selectionCircles.action === CircleAction.REMOVE
                    ? defaultDiagonalCorner
                    : undefined,
              }
            )
        : undefined,
      [StepName.designCustomSide]: selectedCustomSide
        ? (drawingShape) =>
            drawingTool.addCirclesToEdgesOfShape(
              canvas,
              drawingShape,
              selectedCustomSide,
              selectionCircles.action
            )
        : undefined,
    }),
    [
      canvas,
      defaultCornerRadii,
      defaultDiagonalCorner,
      selectedCornerRadius,
      selectedCustomSide,
      selectedDiagonalCorner,
      selectionCircles.action,
    ]
  );

  const selectionCircleRenderer = get(selectionCircleRenderByStep, currentStep);

  const renderSelectionCircles = useCallback(() => {
    const mustRenderSelectionCircles =
      selectionCircles.selection !== SELECTION_CIRCLES.FOR_NONE &&
      !!selectionCircleRenderer;

    if (mustRenderSelectionCircles) {
      drawingTool.removeObjectByName(
        canvas,
        CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE
      );
      drawingShapes.forEach(selectionCircleRenderer);
    }

    return mustRenderSelectionCircles;
  }, [
    selectionCircleRenderer,
    canvas,
    drawingShapes,
    selectionCircles.selection,
  ]);

  useEffect(() => {
    if (renderSelectionCircles()) {
      canvas.renderAll();
    }
  }, [canvas, renderSelectionCircles]);

  return {
    renderSelectionCircles,
  };
};
